import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AdminSurface from '../../../../components/admin/AdminSurface';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import type { StoreLocation } from '../../../../types/features/storeDetails/locations';
import { useMapNewRegister } from '../hooks/useMapNewRegister';

type StoreDetailsMapNewRegisterCardProps = {
  storeId: string;
  locations: StoreLocation[];
  onCreated: () => void | Promise<void>;
};

const StoreDetailsMapNewRegisterCard: React.FC<StoreDetailsMapNewRegisterCardProps> = ({
  storeId,
  locations,
  onCreated,
}) => {
  const {
    error,
    handleSubmit,
    registerId,
    registerOptions,
    registerOptionsError,
    registerOptionsLoading,
    setRegisterId,
    setStoreLocationId,
    storeLocationId,
    submitting,
  } = useMapNewRegister({ storeId, onCreated });

  return (
    <AdminSurface sx={{ overflow: 'hidden' }}>
      <Box sx={{ backgroundColor: '#F8FBFF', borderBottom: `1px solid ${adminColors.divider}`, px: 2, py: 2.5 }}>
        <Typography sx={{ ...adminTypography.sectionTitle, color: '#2C6CA0' }}>
          Map New Register
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: '1.25fr 1fr auto' },
          p: 2,
        }}
      >
        <Autocomplete
          freeSolo
          fullWidth
          options={registerOptions}
          inputValue={registerId}
          value={registerId || null}
          onInputChange={(_, value, reason) => {
            if (reason === 'clear') {
              setRegisterId('');
              return;
            }

            setRegisterId(value);
          }}
          onChange={(_, value) => {
            setRegisterId(typeof value === 'string' ? value : value || '');
          }}
          disabled={submitting || registerOptionsLoading}
          loading={registerOptionsLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Register ID"
              placeholder="Select or enter register ID"
              size="small"
              error={Boolean(error)}
              helperText={error || registerOptionsError || (registerOptions.length === 0 ? 'No register IDs found. You can still type one manually.' : 'Choose from the list or type a register ID.')}
              FormHelperTextProps={{
                sx: {
                  color: '#808080',
                  fontSize: '12px',
                  fontStyle: 'italic',
                },
              }}
            />
          )}
        />
        <FormControl fullWidth size="small" disabled={submitting}>
          <InputLabel id="store-location-select-label">Select Location</InputLabel>
          <Select
            labelId="store-location-select-label"
            label="Select Location"
            value={storeLocationId}
            disabled={submitting || locations.length === 0}
            onChange={(event) => setStoreLocationId(event.target.value as string)}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 280,
                },
              },
            }}
          >
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.location_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="medium"
          onClick={handleSubmit}
          disabled={submitting || locations.length === 0}
          sx={{
            alignSelf: 'start',
            backgroundColor: adminColors.primary,
            boxShadow: 'none',
            height: 40,
            minWidth: 112,
            px: 1.5,
            '&:hover': { backgroundColor: adminColors.primaryHover },
          }}
        >
          Map Register
        </Button>
      </Box>
    </AdminSurface>
  );
};

export default React.memo(StoreDetailsMapNewRegisterCard);
